import { useState } from 'react';

import { bbox } from '@turf/turf';

import { GEOJSON_PROPERTY } from 'components/Map/hooks/useFeatures/constants';
import useMapContext from 'components/Map/hooks/useMapContext';

import {
    CLUSTER_FEATURE_SOURCE,
    FIT_TO_DATA_PADDING,
    OFFSET_MAP_CENTER,
    STATIC_ZOOM
} from './constants';
import type {
    GeoJSONFeature,
    GeoJSONSource,
    LatLngBoundsLike,
    Map,
    MapMouseEvent,
    MapZoomEvent,
    Point
} from './types';

const useClusters = () => {
    const [isClustered, setIsClustered] = useState(true);
    const { features } = useMapContext();
    const { getFeatureById } = features;

    const updateClusterState = (zoom: number) => {
        const shouldCluster = zoom < STATIC_ZOOM.min;

        setIsClustered(previousIsClustered => {
            if (previousIsClustered === shouldCluster) {
                return previousIsClustered;
            }

            return shouldCluster;
        });
    };

    const handleZoom = (event: MapZoomEvent) => {
        updateClusterState(event.target.getZoom());
    };

    const initializeZoomState = (map: Map) => {
        updateClusterState(map.getZoom());
    };

    const handleClusterClick = (event: MapMouseEvent & { features?: GeoJSONFeature[] }) => {
        const mapFeature = event.features?.[0];
        const clusterId = mapFeature?.properties?.cluster_id as number | undefined;

        if (clusterId === undefined) {
            return;
        }

        const map = event.target;

        const source = map.getSource(CLUSTER_FEATURE_SOURCE) as GeoJSONSource | undefined;

        source?.getClusterExpansionZoom?.(clusterId, (error, zoom) => {
            if (error || typeof zoom !== 'number' || !mapFeature?.geometry) {
                return;
            }

            const geometry = mapFeature.geometry as Point;
            const [longitude, latitude] = geometry.coordinates;

            map.easeTo({ center: [longitude, latitude], offset: OFFSET_MAP_CENTER, zoom });
        });
    };

    const handleUnclusteredClick = (event: MapMouseEvent & { features?: GeoJSONFeature[] }) => {
        const mapFeature = event.features?.[0];
        const featureId = mapFeature?.properties?.[GEOJSON_PROPERTY.REFERENCED_BOUNDARY_ID];
        const feature = getFeatureById(featureId as string);

        if (!feature) {
            return;
        }

        const [minLng, minLat, maxLng, maxLat] = bbox(feature);

        const bounds = [
            [minLng, minLat],
            [maxLng, maxLat]
        ] as LatLngBoundsLike;

        event.target.fitBounds(bounds, { linear: true, padding: FIT_TO_DATA_PADDING });
    };

    return {
        handleClusterClick,
        handleUnclusteredClick,
        handleZoom,
        initializeZoomState,
        isClustered
    };
};

export default useClusters;
